<template>
  <main class="mx-auto py-16">
    <div class="container mx-auto">
      <CommonBackToButton
        text-to="About Us"
        url="/about"
        class="pt-5 md:pt-12"
      />
    </div>
    <section
      class="vision-mission container flex flex-col gap-16 mt-10 mx-auto"
    >
      <template v-if="isLoading">
        <div class="vision flex flex-col gap-6">
          <UtilsSkeletonLoading class="w-2/5 md:w-1/5 h-8" />
          <UtilsSkeletonLoading class="w-full lg:w-1/2 md:h-32" />
        </div>
        <div class="mission flex flex-col gap-6">
          <UtilsSkeletonLoading class="w-2/5 md:w-1/5 h-8" />
          <UtilsSkeletonLoading class="w-full lg:w-1/2 md:h-32" />
        </div>
      </template>
      <template v-else>
        <div class="vision flex flex-col gap-6">
          <h3 class="text-base sm:text-[22px] sm:leading-8 text-bw-800">
            Our Vision
          </h3>
          <div
            class="vision-text font-medium text-bw-900 text-[22px] sm:text-[40px] !leading-normal sm:leading-[50px] w-full lg:w-1/2"
            v-html="purposesData.attributes?.vision"
          ></div>
        </div>
        <div class="mission flex flex-col gap-6">
          <h3 class="text-base sm:text-[22px] sm:leading-8 text-bw-800">
            Our Mission
          </h3>
          <div
            class="mission-text text-[22px] sm:text-[40px] !leading-normal sm:leading-[50px] font-medium w-full lg:w-4/5 text-bw-900"
            v-html="purposesData.attributes?.mission"
          ></div>
        </div>
      </template>
    </section>

    <section
      class="collab-innov flex flex-col lg:flex-row py-8 md:py-16 pl-0 gap-12 mt-10 relative max-md:overflow-hidden"
    >
      <template v-if="isLoading">
        <div
          class="bg-linear-ci value p-4 py-10 sm:py-14 md:py-20 flex w-[90%] sm:w-4/5 md:w-2/5 rounded-tr-2xl rounded-br-2xl font-medium text-[22px] md:text-4xl lg:text-[40px] leading-normal lg:leading-[50px]"
        >
          <UtilsSkeletonLoading class="w-4/5 h-64" />
        </div>
        <div
          class="flex flex-col justify-evenly w-full md:w-3/5 px-4 md:pr-5 md:pl-0 gap-y-4"
        >
          <div
            v-for="i in 5"
            :key="i"
            class="flex justify-between ci-card items-center gap-1 md:gap-4 lg:gap-10 w-full"
          >
            <UtilsSkeletonLoading class="w-15 h-15" />
            <UtilsSkeletonLoading class="w-1/5 h-6" />
            <UtilsSkeletonLoading class="w-3/5 h-16" />
          </div>
        </div>
      </template>
      <template v-else>
        <!-- <div
          class="bg-linear-ci py-6 lg:py-20 w-[90%] h-[87%] sm:w-4/5 lg:w-2/5 absolute top-10 left-0 rounded-tr-2xl rounded-br-2xl"
        ></div> -->
        <div class="flex flex-col lg:flex-row pl-0 gap-12 container mx-auto">
          <div
            class="value py-10 sm:py-14 md:py-20 flex w-full sm:w-4/5 lg:w-2/5 rounded-tr-2xl rounded-br-2xl text-white font-medium text-[22px] md:text-[40px] leading-normal md:leading-[50px] z-[3]"
            v-html="purposesData.attributes?.our_value"
          ></div>
          <div
            class="flex flex-col justify-evenly w-full lg:w-3/5 lg:pl-0 gap-y-4 lg:gap-y-2"
          >
            <div
              v-for="list in valuesList"
              :key="list.id"
              class="flex justify-between ci-card items-center gap-1 md:gap-4 lg:gap-10 w-full"
            >
              <div class="ci-icon">
                <NuxtImg
                  format="webp"
                  srcset=""
                  loading="lazy"
                  :src="list?.icon?.data?.attributes?.url || '/'"
                  :alt="list?.title"
                  :title="list?.title"
                  class="w-10 md:w-15"
                />
              </div>
              <div class="ci-title w-[96px]">
                <h4
                  class="text-bw-900 text-sm md:text-base sm:text-lg font-medium"
                >
                  {{ list.title }}
                </h4>
              </div>
              <div class="ci-desc">
                <p class="text-sm md:text-base text-bw-800 font-normal">
                  {{ list.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </template>
    </section>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo
const mainBanner = global.mainBanner

const isLoading = ref(true)

const route = useRoute()
const SEOMeta = ref({})

const purposesData = ref([])
const valuesList = ref([])

const [purpose, seo] = await Promise.all([
  useSuitmediaApiData(`/api/purpose`, {
    query: {
      'filters[url]': '/career',
      'fields[0]': 'id',
      'fields[1]': 'vision',
      'fields[2]': 'mission',
      'fields[3]': 'our_value',
      'populate[values][fields][0]': 'id',
      'populate[values][fields][1]': 'title',
      'populate[values][fields][2]': 'icon',
      'populate[values][fields][3]': 'description',
      'populate[values][populate][4]': 'icon'
    }
  }),
  useSuitmediaApiData(`/api/seo-metas`, {
    query: {
      'filters[url]': route.path,
      populate: 'seo.image'
    }
  })
])

isLoading.value = purpose.pending.value

if (purpose.error.value) {
  throw createError({
    statusCode: 404,
    message: `Purpose does not exist`
  })
}

SEOMeta.value = seo.data.value.data[0]
purposesData.value = purpose.data.value.data
valuesList.value = purpose.data.value.data.attributes?.values

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: SEOMeta?.value?.attributes?.url || 'suitmedia.com',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})

useSeoMeta({
  title: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: SEOMeta?.value?.attributes?.seo?.open_graph_type,
  twitterTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})
</script>

<style lang="scss" scoped>
.bg-linear-ci {
  background: linear-gradient(324.36deg, #111620 28.59%, #595959 163.06%),
    linear-gradient(0deg, #bebebe, #bebebe);
}

.value {
  position: relative;

  @media screen and (min-width: 1024px) {
    position: static;
  }

  &::before {
    content: '';
    background: linear-gradient(324.36deg, #111620 28.59%, #595959 163.06%),
      linear-gradient(0deg, #bebebe, #bebebe);
    z-index: 1;

    top: 0;
    left: -32px;
    position: absolute;

    width: 122%;
    height: 100%;

    @media screen and (min-width: 640px) {
      left: -64px;
      width: 152%;
    }

    @media screen and (min-width: 768px) {
      left: -128px;
      width: 100%;
      border-radius: 0 16px 16px 0;
    }

    @media screen and (min-width: 1024px) {
      width: 40%;
      height: 81%;

      top: 55px;
      left: 0;
    }
  }

  // @media screen and (min-width: 768px) {
  // 	background: none;
  // }
}

:deep(.ci-icon) {
  svg {
    width: 60px;
    height: 60px;
  }
}

.underline {
  text-decoration-skip-ink: none;
}

.ci-desc {
  width: 60%;
}

:deep(.vision-text),
:deep(.mission-text),
:deep(.value) {
  span {
    text-decoration: underline;
    text-decoration-thickness: 5px;
    text-decoration-color: #ff6700;
  }
}

:deep(.value) {
  h2 {
    width: 100%;
    z-index: 3;

    @media screen and (min-width: 768px) {
      width: 75%;
    }
  }
}
</style>
